import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

import "./about.css";
import Divider from "../components/Divider";
import usePageMeta from "../hooks/usePageMeta";
import { experience, formatMonth, sortExperience } from "../data/experience";

// Rows for the "Currently" section. Rows with an empty value are hidden.
const currently = [
	{ label: "Head of Product", value: "Adler One (remote)" },
	{ label: "UX Designer", value: "uxKraft (remote, New Jersey, US)" },
	{ label: "Based in", value: "Islamabad, Pakistan" },
];

const reveal = {
	initial: { opacity: 0, y: 100 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.6 },
};

function Reveal({ as = "div", className, children }) {
	const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.4 });
	const Component = motion[as];

	return (
		<Component
			ref={ref}
			className={className}
			initial={reveal.initial}
			animate={inView ? reveal.animate : reveal.initial}
			transition={reveal.transition}
		>
			{children}
		</Component>
	);
}

export default function About() {
	usePageMeta(
		"About",
		"About Muhammad Munaf, a product engineer in Islamabad, Pakistan, with a background in product design and UX."
	);

	return (
		<main id="main-content" tabIndex={-1}>
			<div className="starting-section">
				<motion.h1
					className="about-title"
					initial={reveal.initial}
					animate={reveal.animate}
					transition={{ duration: 0.7 }}
				>
					Hi, I’m Muhammad Munaf, <br /> a product{" "}
					<span className="fancy-text">engineer.</span>
				</motion.h1>
			</div>
			<AboutSection />
			<CurrentlySection />
			<ExperienceSection />
		</main>
	);
}

function AboutSection() {
	const paragraphs = [
		"I’m a product engineer based in Islamabad, Pakistan, with a background in product design and UX.",
		"Over the past few years I’ve designed websites, web apps and internal tools for companies and clients in Pakistan, Europe and the US. Today I lead product at Adler One and work as a UX designer at uxKraft.",
		"My design experience shapes how I build: understanding the problem, making the experience clear, and refining it through feedback.",
		"This website brings together selected work from my design career.",
	];

	return (
		<section className="about-section full-page" aria-labelledby="about-title">
			<div className="about-section-content">
				<Reveal as="h2" className="section-label">
					<span id="about-title">About</span>
				</Reveal>

				<div className="content">
					{paragraphs.map((text) => (
						<Reveal as="p" key={text}>
							{text}
						</Reveal>
					))}
				</div>
			</div>
		</section>
	);
}

function CurrentlySection() {
	const rows = currently.filter((row) => row.value);

	return (
		<section
			className="about-section full-page white-bg currently"
			aria-labelledby="currently-title"
		>
			<div className="about-section-content">
				<h2 id="currently-title" className="section-label black-label">
					Currently
				</h2>
				<dl className="content currently-list">
					{rows.map((row) => (
						<div className="currently-row" key={row.label}>
							<dt>{row.label}</dt>
							<dd>{row.value}</dd>
						</div>
					))}
				</dl>
			</div>
		</section>
	);
}

function ExperienceItem({ company, title, start, end, arrangement, description }) {
	return (
		<Reveal>
			<div className="experience-item">
				<div className="item-content">
					<h3>{company}</h3>
					<p>{title}</p>
					{description && <p className="description">{description}</p>}
				</div>
				<p className="date">
					{formatMonth(start)} –{" "}
					{end ? (
						formatMonth(end)
					) : (
						<span className="fancy-text-strong">Present</span>
					)}
					{arrangement && ` · ${arrangement}`}
				</p>
			</div>
		</Reveal>
	);
}

function ExperienceSection() {
	const items = sortExperience(experience);

	return (
		<section
			className="about-section full-page white-bg"
			aria-labelledby="experience-title"
		>
			<div className="about-section-content">
				<h2 id="experience-title" className="section-label black-label">
					Experience
				</h2>

				<div className="content">
					{items.map((item, index) => (
						<React.Fragment key={item.company + item.start}>
							<ExperienceItem {...item} />
							{index !== items.length - 1 && <Divider />}
						</React.Fragment>
					))}
				</div>
			</div>
		</section>
	);
}
